import type { GatePass } from "./race-types";

export interface LapRecord {
  lap: number;
  ms: number;
  sectors: Array<number | null>;
  sectorWasPB: boolean[];
}

export interface RaceState {
  nGates: number;
  nextPassIdx: number;
  lapsCompleted: LapRecord[];
  currentLap: number;
  currentLapStartT: number | null;
  currentLapSectors: Array<number | null>;
  currentLapSectorWasPB: boolean[];
  bestSectorMs: Array<number | null>;
  bestSectorLap: Array<number | null>;
  bestLapMs: number | null;
  bestLapNum: number | null;
  lastLapMs: number | null;
  lastPass: GatePass | null;
  passedGates: Set<number>;
  passCount: Map<number, number>;
  stream: StreamEvent[];
}

export type StreamEvent =
  | { kind: "lap-start"; t: number; gate: number; lap: number }
  | { kind: "warmup"; t: number; gate: number }
  | {
      kind: "sector";
      detail: "sector-overall" | "sector-slow";
      t: number;
      sectorIdx: number;
      ms: number;
      delta: number | null;
      gate: number;
      lap: number;
    }
  | { kind: "lap-done"; t: number; lap: number; ms: number }
  | { kind: "miss"; t: number; gate: number; expected: number };

export function createRaceState(nGates: number): RaceState {
  return {
    nGates,
    nextPassIdx: 0,
    lapsCompleted: [],
    currentLap: 0,
    currentLapStartT: null,
    currentLapSectors: new Array(nGates).fill(null),
    currentLapSectorWasPB: new Array(nGates).fill(false),
    bestSectorMs: new Array(nGates).fill(null),
    bestSectorLap: new Array(nGates).fill(null),
    bestLapMs: null,
    bestLapNum: null,
    lastLapMs: null,
    lastPass: null,
    passedGates: new Set(),
    passCount: new Map(),
    stream: [],
  };
}

function pushStream(state: RaceState, ev: StreamEvent) {
  state.stream.unshift(ev);
  if (state.stream.length > 40) state.stream.pop();
}

/**
 * Process one gate pass through the lap/sector state machine. Ported from
 * PrototypeDroneRacing/web/app.js::onGatePass.
 */
export function onGatePass(state: RaceState, pass: GatePass): void {
  const { nGates } = state;
  const gidx = pass.gate_idx;

  if (state.lastPass === null) {
    state.lastPass = pass;
    state.passCount.set(gidx, (state.passCount.get(gidx) ?? 0) + 1);
    if (gidx === 1) {
      state.currentLap = 1;
      state.currentLapStartT = pass.t;
      state.currentLapSectors = new Array(nGates).fill(null);
      state.currentLapSectorWasPB = new Array(nGates).fill(false);
      state.passedGates = new Set([1]);
      pushStream(state, { kind: "lap-start", t: pass.t, gate: gidx, lap: 1 });
    } else {
      pushStream(state, { kind: "warmup", t: pass.t, gate: gidx });
    }
    return;
  }

  const prev = state.lastPass;
  const expected = (prev.gate_idx % nGates) + 1;

  if (gidx === expected && state.currentLap > 0) {
    const sectorIdx = prev.gate_idx; // sector prev→current
    const sectorMs = pass.t - prev.t;
    state.currentLapSectors[sectorIdx - 1] = sectorMs;

    const prevBest = state.bestSectorMs[sectorIdx - 1];
    const wasPB = prevBest === null || sectorMs < prevBest;
    state.currentLapSectorWasPB[sectorIdx - 1] = wasPB;

    let detail: "sector-overall" | "sector-slow";
    if (wasPB) {
      state.bestSectorMs[sectorIdx - 1] = sectorMs;
      state.bestSectorLap[sectorIdx - 1] = state.currentLap;
      detail = "sector-overall";
    } else {
      detail = "sector-slow";
    }

    pushStream(state, {
      kind: "sector",
      detail,
      t: pass.t,
      sectorIdx,
      ms: sectorMs,
      delta: prevBest === null ? null : sectorMs - prevBest,
      gate: gidx,
      lap: state.currentLap,
    });
    state.passedGates.add(gidx);
    state.passCount.set(gidx, (state.passCount.get(gidx) ?? 0) + 1);

    if (prev.gate_idx === nGates && gidx === 1) {
      const lapMs = pass.t - (state.currentLapStartT ?? pass.t);
      state.lastLapMs = lapMs;
      if (state.bestLapMs === null || lapMs < state.bestLapMs) {
        state.bestLapMs = lapMs;
        state.bestLapNum = state.currentLap;
      }
      state.lapsCompleted.push({
        lap: state.currentLap,
        ms: lapMs,
        sectors: state.currentLapSectors.slice(),
        sectorWasPB: state.currentLapSectorWasPB.slice(),
      });
      pushStream(state, { kind: "lap-done", t: pass.t, lap: state.currentLap, ms: lapMs });
      state.currentLap += 1;
      state.currentLapStartT = pass.t;
      state.currentLapSectors = new Array(nGates).fill(null);
      state.currentLapSectorWasPB = new Array(nGates).fill(false);
      state.passedGates = new Set([1]);
    }
  } else if (gidx === 1 && state.currentLap === 0) {
    state.currentLap = 1;
    state.currentLapStartT = pass.t;
    state.currentLapSectors = new Array(nGates).fill(null);
    state.currentLapSectorWasPB = new Array(nGates).fill(false);
    state.passedGates = new Set([1]);
    pushStream(state, { kind: "lap-start", t: pass.t, gate: gidx, lap: 1 });
    state.passCount.set(gidx, (state.passCount.get(gidx) ?? 0) + 1);
  } else {
    pushStream(state, { kind: "miss", t: pass.t, gate: gidx, expected });
    state.passCount.set(gidx, (state.passCount.get(gidx) ?? 0) + 1);
  }

  state.lastPass = pass;
}

/** Process every pass whose timestamp is ≤ currentT that we haven't yet processed. */
export function advanceState(state: RaceState, passes: GatePass[], currentT: number): void {
  while (state.nextPassIdx < passes.length && passes[state.nextPassIdx].t <= currentT) {
    onGatePass(state, passes[state.nextPassIdx]);
    state.nextPassIdx += 1;
  }
}

export function classifySector(
  state: RaceState,
  lapNum: number,
  sectorIdx: number,
  sectorMs: number | null,
  wasPB: boolean,
  dbBestSectorMs: number | null = null,
): "overall" | "pb" | "slow" | null {
  if (sectorMs === null || sectorMs === undefined) return null;
  // Purple only when this lap owns the session-best for the sector AND that
  // time also beats the DB best (or there is no DB best yet). Otherwise a
  // session PB falls back to green, matching the user's DB comparison rule.
  if (state.bestSectorLap[sectorIdx - 1] === lapNum) {
    const sessionBest = state.bestSectorMs[sectorIdx - 1];
    if (
      sessionBest !== null &&
      (dbBestSectorMs === null || sessionBest < dbBestSectorMs)
    ) {
      return "overall";
    }
    return "pb";
  }
  if (wasPB) return "pb";
  return "slow";
}

export function formatMs(ms: number | null | undefined, dashIfNull = true) {
  if (ms === null || ms === undefined) return dashIfNull ? "--:--.---" : "";
  const sign = ms < 0 ? "-" : "";
  const abs = Math.abs(ms);
  const m = Math.floor(abs / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  const mm = Math.floor(abs % 1000);
  return `${sign}${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(mm).padStart(3, "0")}`;
}

export function formatSector(ms: number | null | undefined) {
  if (ms === null || ms === undefined) return "--.---";
  return (ms / 1000).toFixed(3);
}

export function formatDelta(ms: number | null | undefined) {
  if (ms === null || ms === undefined) return "±0.000";
  const sign = ms < 0 ? "-" : "+";
  return `${sign}${(Math.abs(ms) / 1000).toFixed(3)}`;
}
